import { ApiResponse, UISchema, FileArchiveDefRow } from "@bonusmap/api-types";

export interface FileViewItem {
    id: number;
    file_name: string;
    file_type: string;
    file_size: number;
    file_size_formatted: string;
    label?: string;
    description?: string;
    tags: string[];
    iconClass: string;
    actionUrl: string; // "open" | "download"
    fullUrl: string; // URL completo per accesso al file
}

/**
 * Questo helper si occupa di recuperare i dati relativi ai files allegati
 * trasformando gli ID dei files in dati completi con informazioni per la visualizzazione
 */
export async function retrieveFiles(
    dataSource: UISchema,
    fieldName: string = "files"): Promise<FileViewItem[]> {

    const uiField = dataSource[fieldName];
    if (!uiField) {
        return [];
    }

    const fileIds = uiField.value;
    if (!fileIds || !Array.isArray(fileIds)) {
        return [];
    }

    // Filtra solo ID numerici validi
    const validIds = fileIds.filter((id: any) => typeof id === 'number' && id > 0);
    if (validIds.length === 0) {
        return [];
    }

    // Recupera le informazioni dei files tramite API
    const apiResponse: ApiResponse<FileArchiveDefRow[]> = await window.api.manager.fileArchive.getByIds(validIds) as ApiResponse<FileArchiveDefRow[]>;

    if (!apiResponse?.success || !apiResponse?.data) {
        return [];
    }

    // Trasforma i dati in formato view-friendly
    const result: FileViewItem[] = apiResponse.data.map(file => {
        const file_size_formatted = formatFileSize(file.file_size);
        const iconClass = getFileIconClass(file);
        const actionUrl = getFileActionUrl(file);
        const fullUrl = `/file/${actionUrl}/${file.id}`;

        return {
            id: file.id,
            file_name: file.file_name,
            file_type: file.file_type,
            file_size: file.file_size,
            file_size_formatted,
            label: file.label || undefined,
            description: file.description || undefined,
            tags: file.tags || [],
            iconClass,
            actionUrl,
            fullUrl
        };
    });

    return result;
}

/**
 * Formatta la dimensione del file in formato leggibile
 */
function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Determina la classe CSS per l'icona del file
 */
function getFileIconClass(file: { file_name: string; file_type: string }): string {
    let cls = "file";
    const extension = file.file_name.split('.').pop()?.toLowerCase() ?? "";

    if (file.file_type.startsWith('image/') || ['png', 'jpg', 'heic', 'bmp', 'webp', 'gif', 'jpeg'].includes(extension)) {
        cls += " file-image";
    }
    else if (file.file_type === 'application/pdf' || extension === 'pdf') {
        cls += " file-pdf";
    }
    else if (['doc', 'docx', 'odt', 'txt', 'rtf', 'pages', 'tex', 'wpd', 'wps'].includes(extension) ||
             ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'application/vnd.oasis.opendocument.text', 'text/plain', 'text/rtf'].includes(file.file_type)) {
        cls += " file-word";
    }
    else if (['xls', 'xlsx', 'csv', 'ods', 'tsv', 'xlsm', 'xlsb'].includes(extension) ||
             ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'text/csv', 'application/vnd.oasis.opendocument.spreadsheet'].includes(file.file_type)) {
        cls += " file-excel";
    }
    else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension) ||
             ['application/zip', 'application/x-rar-compressed', 'application/gzip'].includes(file.file_type)) {
        cls += " file-zip";
    }

    return cls;
}

/**
 * Determina l'azione per il file (aprire nel browser o forzare download)
 */
function getFileActionUrl(file: { file_name: string; file_type: string }): string {
    const extension = file.file_name.split('.').pop()?.toLowerCase() ?? "";

    // Mimetype che si aprono direttamente nel browser
    const browserSupportedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
        'application/pdf'
    ];

    const browserSupportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'pdf', 'txt', 'html'];

    if (browserSupportedTypes.includes(file.file_type) || browserSupportedExtensions.includes(extension)) {
        return "open";
    }

    return "download";
}